/**
 * Theme persistence shared by the pre-paint inline script (app/layout.tsx) and
 * the client-side toggle. Deliberately tiny and dependency-free: the site has
 * exactly two themes, defaults to dark (brand-first, not system preference),
 * and remembers the visitor's choice in localStorage.
 *
 * The theme is expressed as `data-theme` on <html>; every token in
 * app/globals.css resolves against the nearest [data-theme] ancestor, so a
 * section can also pin itself dark with its own `data-theme="dark"`.
 */

export type Theme = 'light' | 'dark'

export const THEME_KEY = 'rf-theme'
export const DEFAULT_THEME: Theme = 'dark'

const THEME_COLOR: Record<Theme, string> = { dark: '#000000', light: '#ffffff' }

export function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark'
}

/** Current theme as rendered, falling back to the default before hydration. */
export function getTheme(): Theme {
  if (typeof document === 'undefined') return DEFAULT_THEME
  const current = document.documentElement.dataset.theme
  return isTheme(current) ? current : DEFAULT_THEME
}

/** The visitor's stored preference, if any. Storage access can throw (Safari private mode). */
export function getStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(THEME_KEY)
    return isTheme(stored) ? stored : null
  } catch {
    return null
  }
}

/** Apply a theme to the document without persisting it (used to pin admin dark). */
export function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme
  document.documentElement.style.colorScheme = theme
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta) meta.content = THEME_COLOR[theme]
}

/** Apply and persist the visitor's choice. */
export function setTheme(theme: Theme) {
  applyTheme(theme)
  try {
    window.localStorage.setItem(THEME_KEY, theme)
  } catch {
    // Storage unavailable: the choice still holds for this page view.
  }
}

/**
 * Runs before first paint, inlined as the first child of <body>, so a visitor
 * who chose light never sees a dark flash. Kept as a string so it needs no
 * bundling and can't be deferred. Only the two known values are honoured.
 */
export const THEME_INIT_SCRIPT = `(function(){try{var t=localStorage.getItem('${THEME_KEY}');if(t==='light'||t==='dark'){document.documentElement.dataset.theme=t;document.documentElement.style.colorScheme=t}}catch(e){}})()`
